import { useState } from "react";
import SbuxDialog2 from "../../../../components/SbuxDialog2";
import SbuxStackSnackbar from "../../../../components/SbuxStackSnackbar";
import SbuxTranslate from "../../../../components/SbuxTranslate";
import ClosureMsgAdd from "../table/closureMsg/ClosureMsgAdd";
import ClosureMsgEdit from "../table/closureMsg/ClosureMsgEdit";
import ClosureSingleDeleteDialog from "./ClosureSingleDeleteDialog";
import { closureMsgRepeatPattern } from "../utils";
import { createOrUpdateClosureMsg } from "../../../../services/closureMsg";
import { Box, Button, DialogContent, DialogActions } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../store/types";
import { SNACK_BARS_SEVERITY_ERROR } from "../../../../constants";
import dayjs from "../../../../utils/dayjs";
import { getMediaMgtDataSelector } from "../../../../selectors/mediaMgtSelector";
import {
  MediaMgtDataContainer,
  MediaItem,
} from "../../../../slices/mediaMgtSlice";

interface Language {
  code?: string;
  value: string;
  name: string;
  [key: string]: unknown;
}

interface Props {
  title: React.ReactNode;
  open: boolean;
  row: Record<string, unknown>;
  toggleDialogOpen?: () => void;
  handleRadioOkClick?: (value: string) => void;
  business?: unknown;
  languages?: Language[];
  isAddNew?: boolean;
  globalConfigUi?: unknown;
  isSingleInstance?: boolean;
}

const ClosureMsgDialog = ({
  title,
  open,
  row,
  toggleDialogOpen,
  business,
  languages,
  isAddNew,
  globalConfigUi,
  isSingleInstance,
}: Props) => {
  const mediaMgtData = useAppSelector(getMediaMgtDataSelector);
  const dispatch = useAppDispatch();
  const [closureMsgRow, setClosureMsgRow] =
    useState<Record<string, unknown>>(row);
  const [isDelete, setIsDelete] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [isAudioChanged, setIsAudioChanged] = useState(false);

  const handleClose = (_event: unknown, reason?: string) => {
    if (reason === "clickaway") return;
    setOpenSnackBar(false);
  };

  const handleDeleteOnClick = () => {
    setIsDelete((prevState) => !prevState);
  };

  const getMediaItems = (): MediaItem[] => {
    if (!mediaMgtData) return [];
    if (Array.isArray(mediaMgtData)) return mediaMgtData as MediaItem[];
    return (mediaMgtData as MediaMgtDataContainer).data ?? [];
  };

  const handleSave = async () => {
    let result: Record<string, unknown>,
      repeatPattern = null;
    repeatPattern =
      closureMsgRow && closureMsgRow.type === "Repeat"
        ? closureMsgRepeatPattern(
            closureMsgRow as unknown as Parameters<
              typeof closureMsgRepeatPattern
            >[0],
            isAddNew,
          )
        : null;
    if (closureMsgRow.isReccuringOption2Selected && repeatPattern) {
      const rp = repeatPattern as unknown as Record<string, unknown>;
      delete rp["until"];
    }

    if (isAddNew) {
      result = repeatPattern
        ? {
            hoursCode: Number(closureMsgRow.hoursCode),
            groupName: closureMsgRow.groupName,
            description: closureMsgRow.description,
            type: closureMsgRow.type,
            category: closureMsgRow.category,
            startDate: dayjs(closureMsgRow.startDate as string).format(
              "YYYY-MM-DD",
            ),
            startTime: dayjs(
              closureMsgRow.startTime as string,
              "hh:mm A",
            ).format("hh:mm A"),
            endDate: dayjs(closureMsgRow.endDate as string).format(
              "YYYY-MM-DD",
            ),
            endTime: dayjs(closureMsgRow.endTime as string, "hh:mm A").format(
              "hh:mm A",
            ),
            startTimeZone: closureMsgRow.startTimeZone,
            endTimeZone: closureMsgRow.endTimeZone,
            ...(closureMsgRow.languageMessage as Record<string, unknown>),
            repeatPattern: repeatPattern,
          }
        : {
            hoursCode: Number(closureMsgRow.hoursCode),
            groupName: closureMsgRow.groupName,
            description: closureMsgRow.description,
            type: closureMsgRow.type,
            category: closureMsgRow.category,
            startDate: dayjs(closureMsgRow.startDate as string).format(
              "YYYY-MM-DD",
            ),
            startTime: dayjs(
              closureMsgRow.startTime as string,
              "hh:mm A",
            ).format("hh:mm A"),
            endDate: dayjs(closureMsgRow.endDate as string).format(
              "YYYY-MM-DD",
            ),
            endTime: dayjs(closureMsgRow.endTime as string, "hh:mm A").format(
              "hh:mm A",
            ),
            startTimeZone: closureMsgRow.startTimeZone,
            endTimeZone: closureMsgRow.endTimeZone,
            ...(closureMsgRow.languageMessage as Record<string, unknown>),
          };
    } else {
      let occurrences: Record<string, unknown> | null = null;
      const rowOccurrences = closureMsgRow.occurrences as
        | Record<string, Record<string, unknown>>
        | undefined;
      for (const key in rowOccurrences) {
        if (isSingleInstance && row.occurrenceId === key) {
          occurrences = {
            id: key,
            description: closureMsgRow.description,
            startDate: dayjs(closureMsgRow.startDate as string).format(
              "YYYY-MM-DD",
            ),
            startTime: closureMsgRow.startTime
              ? dayjs(closureMsgRow.startTime as string, "hh:mm A").format(
                  "hh:mm A",
                )
              : dayjs(row.startTime as string, "hh:mm A").format("hh:mm A"),
            endDate: dayjs(closureMsgRow.endDate as string).format(
              "YYYY-MM-DD",
            ),
            endTime: closureMsgRow.endTime
              ? dayjs(closureMsgRow.endTime as string, "hh:mm A").format(
                  "hh:mm A",
                )
              : dayjs(row.endTime as string, "hh:mm A").format("hh:mm A"),
            startTimeZone: closureMsgRow.startTimeZone,
            endTimeZone: closureMsgRow.endTimeZone,
            ...(closureMsgRow.languageMessage as Record<string, unknown>),
          };
        }
      }

      if (repeatPattern && isSingleInstance) {
        const cloneRow = { ...row };
        if (cloneRow.occurrences) delete cloneRow.occurrences;
        const updatedRow = {
          ...cloneRow,
          ...(closureMsgRow.languageMessage as Record<string, unknown>),
          languageMessage: closureMsgRow.languageMessage,
        };
        result = {
          ...updatedRow,
          updatedOccurrence: {
            ...occurrences,
          },
        };
      } else if (repeatPattern && !isSingleInstance) {
        result = {
          id: row.id,
          hoursCode: row.hoursCode,
          groupName: row.groupName,
          description: closureMsgRow.description,
          type: row.type,
          category: row.category,
          startDate: dayjs(closureMsgRow.startDate as string).format(
            "YYYY-MM-DD",
          ),
          startTime: closureMsgRow.startTime
            ? dayjs(closureMsgRow.startTime as string, "hh:mm A").format(
                "hh:mm A",
              )
            : dayjs(row.startTime as string, "hh:mm A").format("hh:mm A"),
          endDate: dayjs(closureMsgRow.endDate as string).format("YYYY-MM-DD"),
          endTime: closureMsgRow.endTime
            ? dayjs(closureMsgRow.endTime as string, "hh:mm A").format(
                "hh:mm A",
              )
            : dayjs(row.endTime as string, "hh:mm A").format("hh:mm A"),
          startTimeZone: closureMsgRow.startTimeZone,
          endTimeZone: closureMsgRow.endTimeZone,
          repeatPattern: repeatPattern,
          updateOccurrences:
            row.repeatPattern !== repeatPattern ||
            dayjs(row.startDate as string).format("YYYY-MM-DD") !==
              dayjs(closureMsgRow.startDate as string).format("YYYY-MM-DD") ||
            dayjs(row.endDate as string).format("YYYY-MM-DD") !==
              dayjs(closureMsgRow.endDate as string).format("YYYY-MM-DD") ||
            dayjs(row.startTime as string, "hh:mm A").format("hh:mm A") !==
              dayjs(closureMsgRow.startTime as string, "hh:mm A").format(
                "hh:mm A",
              ) ||
            dayjs(row.endTime as string, "hh:mm A").format("hh:mm A") !==
              dayjs(closureMsgRow.endTime as string, "hh:mm A").format(
                "hh:mm A",
              ) ||
            row.startTimeZone !== closureMsgRow.startTimeZone ||
            row.endTimeZone !== closureMsgRow.endTimeZone
              ? true
              : false,
          ...(closureMsgRow.languageMessage as Record<string, unknown>),
        };
      } else {
        result = {
          id: row.id,
          hoursCode: row.hoursCode,
          groupName: row.groupName,
          description: closureMsgRow.description,
          type: row.type,
          category: row.category,
          startDate: dayjs(closureMsgRow.startDate as string).format(
            "YYYY-MM-DD",
          ),
          startTime: closureMsgRow.startTime
            ? dayjs(closureMsgRow.startTime as string, "hh:mm A").format(
                "hh:mm A",
              )
            : dayjs(row.startTime as string, "hh:mm A").format("hh:mm A"),
          endDate: dayjs(closureMsgRow.endDate as string).format("YYYY-MM-DD"),
          endTime: closureMsgRow.endTime
            ? dayjs(closureMsgRow.endTime as string, "hh:mm A").format(
                "hh:mm A",
              )
            : dayjs(row.endTime as string, "hh:mm A").format("hh:mm A"),
          startTimeZone: closureMsgRow.startTimeZone,
          endTimeZone: closureMsgRow.endTimeZone,
          ...(closureMsgRow.languageMessage as Record<string, unknown>),
        };
      }
    }
    dispatch(createOrUpdateClosureMsg(result));
    toggleDialogOpen?.();
  };

  const handleClosureMsgRow = (
    updatedRow: Record<string, unknown>,
    isAddnew?: boolean,
  ) => {
    if (updatedRow) {
      setClosureMsgRow(updatedRow);
      if (updatedRow.isAudioFileChanged) {
        setIsAudioChanged(true);
      }
      const startTime = dayjs(updatedRow.startTime as string, "hh:mm A");
      const endTime = dayjs(updatedRow.endTime as string, "hh:mm A");
      const sDate = dayjs(updatedRow.startDate as string).format("MM/DD/YYYY");
      const eDate = dayjs(updatedRow.endDate as string).format("MM/DD/YYYY");
      const parsedStartDate = dayjs(updatedRow.startDate as string);
      const parsedEndDate = dayjs(updatedRow.endDate as string);
      const updatedEndDateTime = `${eDate} ${updatedRow.endTime as string}`;
      const selectedTimeZoneDateTime = dayjs()
        .tz(updatedRow.startTimeZone as string)
        .format("MM/DD/YYYY hh:mm A");

      const isValidupdatedEndDateTime = dayjs(updatedEndDateTime).isAfter(
        dayjs(selectedTimeZoneDateTime),
      );
      const dateDiff = sDate === eDate ? 0 : 1;
      const isValidStartDate = dayjs(sDate).isValid();
      const isValidEndDate = dayjs(eDate).isValid();
      const isValidStartTime = startTime.isValid();
      const isValidEndTime = endTime.isValid();
      let hasLanguageMessage = false,
        validateStartEndTime = false,
        status = false;

      const rowKeys = Object.keys(row).filter((a) => a.includes("message"));
      const updatedRowKeys = Object.keys(updatedRow).filter((a) =>
        a.includes("message"),
      );

      let isAnyInvalidFile = false;
      const validFiles: MediaItem[] = [];
      const mediaItems = getMediaItems();
      Object.keys(updatedRow).forEach((key) => {
        const msgVal = updatedRow[key] as { value?: string } | undefined;
        if (key.startsWith("message") && msgVal?.value) {
          const result = mediaItems.find(
            (item) => item.filePath === msgVal.value,
          );

          if (!result) {
            isAnyInvalidFile = true;
          } else {
            validFiles.push(result);
          }
        }
      });
      hasLanguageMessage = !isAnyInvalidFile && validFiles.length > 0;

      if (
        (isValidStartTime &&
          isValidEndTime &&
          endTime.isSameOrAfter(startTime)) ||
        (isValidStartTime && isValidEndTime && dateDiff > 0)
      ) {
        validateStartEndTime = true;
      }

      if (
        (isAddnew && !hasLanguageMessage) ||
        !isValidStartDate ||
        !isValidEndDate ||
        parsedEndDate.isBefore(parsedStartDate) ||
        updatedRow.startTime === "" ||
        updatedRow.endTime === "" ||
        !isValidEndTime ||
        !isValidStartTime ||
        !validateStartEndTime ||
        !isValidupdatedEndDateTime ||
        updatedRow.description === "" ||
        updatedRow.category === ""
      ) {
        status = true;
      } else if (
        updatedRow.isAudioFileChanged ||
        isAudioChanged ||
        row.description !== updatedRow.description ||
        (row.endTimeZone !== updatedRow.endTimeZone &&
          isValidupdatedEndDateTime) ||
        ((dayjs(row.startDate as string).format("YYYY-MM-DD") !==
          dayjs(updatedRow.startDate as string).format("YYYY-MM-DD") ||
          dayjs(row.endDate as string).format("YYYY-MM-DD") !==
            dayjs(updatedRow.endDate as string).format("YYYY-MM-DD") ||
          dayjs(row.startTime as string, "hh:mm A").format("hh:mm a") !==
            dayjs(updatedRow.startTime as string, "hh:mm A").format(
              "hh:mm a",
            ) ||
          dayjs(row.endTime as string, "hh:mm A").format("hh:mm a") !==
            dayjs(updatedRow.endTime as string, "hh:mm A").format("hh:mm a") ||
          rowKeys.length !== updatedRowKeys.length ||
          row.repeatPattern !== updatedRow.repeatPattern) &&
          !isAddnew)
      ) {
        status = false;
      } else {
        status = true;
      }
      if (!hasLanguageMessage) {
        status = true;
      }
      status ? setButtonDisabled(true) : setButtonDisabled(false);
    }
  };

  const dialogConfig = {
    title,
  };

  return (
    <>
      <SbuxDialog2
        open={open}
        config={dialogConfig}
        handleOnClose={toggleDialogOpen ?? (() => {})}
      >
        <DialogContent dividers sx={{ overflow: "visible" }}>
          {isAddNew ? (
            <ClosureMsgAdd
              business={
                business as Parameters<typeof ClosureMsgAdd>[0]["business"]
              }
              languages={
                languages as Parameters<typeof ClosureMsgAdd>[0]["languages"]
              }
              row={row as Parameters<typeof ClosureMsgAdd>[0]["row"]}
              handleClosureMsgRow={
                handleClosureMsgRow as Parameters<
                  typeof ClosureMsgAdd
                >[0]["handleClosureMsgRow"]
              }
              globalConfigUi={
                globalConfigUi as Parameters<
                  typeof ClosureMsgAdd
                >[0]["globalConfigUi"]
              }
            />
          ) : (
            <ClosureMsgEdit
              row={closureMsgRow}
              handleClosureMsgRow={handleClosureMsgRow}
              isSingleInstance={isSingleInstance ?? false}
            />
          )}
        </DialogContent>

        <DialogActions>
          <Box className="actionContainer">
            {!isAddNew && (
              <Button
                size="medium"
                onClick={handleDeleteOnClick}
                variant={row.status !== "Expired" ? "contained" : "outlined"}
                disabled={row.status === "Expired"}
                color={row.status !== "Expired" ? "complementary" : "primary"}
              >
                <SbuxTranslate>{"Delete"}</SbuxTranslate>
              </Button>
            )}

            <Button
              onClick={handleSave}
              variant="contained"
              size="medium"
              disabled={buttonDisabled || row.status === "Expired"}
            >
              <SbuxTranslate>{"Save"}</SbuxTranslate>
            </Button>

            <Button
              variant="outlined"
              size="medium"
              onClick={toggleDialogOpen ?? (() => {})}
            >
              <SbuxTranslate>{"Cancel"}</SbuxTranslate>
            </Button>
          </Box>
        </DialogActions>
      </SbuxDialog2>

      {openSnackBar && (
        <SbuxStackSnackbar
          width={380}
          open={openSnackBar}
          anchorOriginHorizontal={`center`}
          message={""}
          severity={SNACK_BARS_SEVERITY_ERROR}
          handleClose={handleClose}
        />
      )}

      {isDelete && (
        <ClosureSingleDeleteDialog
          row={row}
          title={`Delete Closure Message`}
          name={row.description as string}
          open={isDelete}
          isSingleInstance={isSingleInstance ?? false}
          toggleDialogOpen={toggleDialogOpen ?? (() => {})}
          toggleCancelDialog={handleDeleteOnClick}
        />
      )}
    </>
  );
};

export default ClosureMsgDialog;
